import { Facebook, Instagram, Linkedin, Youtube } from "lucide-react";
import { useLocale } from "@/hooks/useLocale";
import { ABOUT_NAV, FEATURE_NAV, RESOURCE_NAV, SITE, SOCIAL_LINKS } from "@/content/site";
import { LocaleLink } from "./LocaleLink";
import { useT } from "@/lib/i18n-dict";
import { Logo } from "./Logo";

const SOCIAL_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  linkedin: Linkedin,
  youtube: Youtube,
  instagram: Instagram,
  facebook: Facebook,
};

function Column({ title, items }: { title: string; items: { label: string; to: string }[] }) {
  const t = useT();
  return (
    <div>
      <h3 className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
        {t(title)}
      </h3>
      <ul className="mt-4 space-y-2.5">
        {items.map((item) => (
          <li key={item.to}>
            <LocaleLink
              to={item.to}
              className="text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              {t(item.label)}
            </LocaleLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SiteFooter() {
  const t = useT();
  const locale = useLocale();
  const visibleSocial = SOCIAL_LINKS.filter((link) => link.id !== "facebook" || locale === "uk");

  return (
    <footer className="border-t border-border bg-surface/40">
      <div className="container-page grid gap-12 py-16 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo />
          <p className="mt-4 max-w-xs text-sm text-muted-foreground">{t(SITE.tagline)}.</p>
          <a
            href={`mailto:${SITE.email}`}
            className="mt-4 inline-block text-sm text-primary hover:underline"
          >
            {SITE.email}
          </a>
          {visibleSocial.length > 0 && (
            <div className="mt-6 flex flex-wrap items-center gap-3">
              {visibleSocial.map((link) => {
                const Icon = SOCIAL_ICONS[link.id];
                return (
                  <a
                    key={link.id}
                    href={locale === "uk" && link.ukHref ? link.ukHref : link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={link.label}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-surface text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
                  >
                    {Icon ? <Icon className="h-5 w-5" /> : null}
                  </a>
                );
              })}
            </div>
          )}
        </div>
        <Column title="Features" items={FEATURE_NAV} />
        <Column
          title="Resources"
          items={[...RESOURCE_NAV, { label: "Pricing", to: "/prices" }]}
        />
        <div>
          <Column title="About us" items={ABOUT_NAV} />
          <a
            href={SITE.supportUrl}
            className="mt-2.5 inline-block text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            {t("Knowledge Base")}
          </a>
          <a
            href={SITE.developerUrl}
            className="mt-2.5 block text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            {t("Developer Portal")}
          </a>
          <LocaleLink
            to="/privacy"
            className="mt-2.5 block text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            {t("Privacy")}
          </LocaleLink>
        </div>
      </div>
      <div className="border-t border-border">
        <div className="container-page flex flex-wrap items-center justify-between gap-3 py-6 text-sm leading-relaxed text-muted-foreground">
          <span>
            © {new Date().getFullYear()} factory.online. {t("All rights reserved.")}
          </span>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event("fo:cookie-settings"))}
            className="transition-colors hover:text-foreground"
          >
            {t("Cookie settings")}
          </button>
          <span>{t("HQ: Dubai Internet City")}</span>
        </div>
      </div>
    </footer>
  );
}
